import type { FC } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { Button } from "@jti/core-components/components/Button";
import { Card } from "@jti/core-components/components/Card";
import type { Address } from "@jti/fe-common";
import { formatFullName } from "@jti/fe-common";
import { useGetProfile } from "@jti/rest-api";
import { AddressInfoBlock, formatPhoneNumber, useApplyAddressOverrides, useTranslations } from "@jti/ui";

import styles from "./ShippingDetailsSection.module.scss";

export type ShippingDetailsSectionProps = {
  shippingAddress?: Address;
  onEdit?: () => void;
  isEditButtonAvailable?: boolean;
  companyName?: string;
  className?: string;
};

export const ShippingDetailsSection: FC<ShippingDetailsSectionProps> = ({
  shippingAddress,
  onEdit,
  isEditButtonAvailable = false,
  companyName,
  className,
}) => {
  const { __ } = useTranslations();
  const config = useConfig();

  const { data: profileData } = useGetProfile();

  const { applyAddressOverrides } = useApplyAddressOverrides();

  return (
    <div className={className}>
      <Card variant="level-1">
        <div className={styles.viewContainer}>
          <div>
            <div className={styles.title}>{__("cart.checkout.shipping_address.title")}</div>
            {shippingAddress && (
              <AddressInfoBlock
                address={applyAddressOverrides(shippingAddress, { provinceCode: profileData?.extra?.province })}
                companyName={companyName}
                hideName
                hideTelephone
              />
            )}
          </div>
          <div>
            <div className={styles.title}>{__("cart.checkout.shipping_contact.title")}</div>
            <div className={styles.contactData}>
              {shippingAddress && (
                <div>
                  {formatFullName(shippingAddress.firstname, shippingAddress.lastname, config.isReversedNameOrder)}
                </div>
              )}

              {shippingAddress && (
                <div>
                  {formatPhoneNumber(
                    shippingAddress.telephone,
                    config.isInternationalPhoneEnabled,
                    config.PHONE_FORMAT
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        {isEditButtonAvailable && onEdit && (
          <div className={styles.editButtonWrapper}>
            <Button data-testid="editShippingAddress" variant="text" onClick={onEdit}>
              {__("cart.checkout.shipping_address.edit")}
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};
